import React from 'react';
import { Box, Typography, alpha, useTheme } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const MONO = "'Consolas', 'SFMono-Regular', monospace";

export function InlineCode({ children }: { children: React.ReactNode }) {
  const theme = useTheme();
  return (
    <Box component="code" sx={{ fontFamily: MONO, fontSize: '0.92em', px: 0.6, py: 0.1, borderRadius: 0.75, bgcolor: alpha(theme.palette.text.primary, 0.08) }}>
      {children}
    </Box>
  );
}

/** Plain text where `backtick` segments render as inline code. */
export function InlineText({ text }: { text: string }) {
  return (
    <>
      {text.split(/(`[^`]+`)/g).map((part, i) =>
        part.startsWith('`') && part.endsWith('`') && part.length > 1
          ? <InlineCode key={i}>{part.slice(1, -1)}</InlineCode>
          : <React.Fragment key={i}>{part}</React.Fragment>)}
    </>
  );
}

function parseFenceMeta(meta?: string) {
  const attrs: Record<string, string> = {};
  (meta || '').split(/\s+/).forEach((p) => {
    const i = p.indexOf('=');
    if (i > 0) attrs[p.slice(0, i)] = p.slice(i + 1);
  });
  if (!attrs.title) return null;
  return attrs.startLineNumber ? `${attrs.title}:${attrs.startLineNumber}-${attrs.endLineNumber ?? attrs.startLineNumber}` : attrs.title;
}

export function CodeBlock({ code, lang, title }: { code: string; lang?: string; title?: string | null }) {
  const theme = useTheme();
  const isDiff = lang === 'diff';
  const lines = code.replace(/\n$/, '').split('\n');
  return (
    <Box sx={{ mb: 1.75 }}>
      {title && <Typography sx={{ fontFamily: MONO, fontSize: 11, color: 'text.secondary', mb: 0.5 }}>{title}</Typography>}
      <Box
        component="pre"
        sx={{
          m: 0, p: 1.5, overflowX: 'auto', borderRadius: 1.5, border: '1px solid', borderColor: 'divider',
          bgcolor: alpha(theme.palette.text.primary, 0.04), fontFamily: MONO, fontSize: 12, lineHeight: 1.55, color: 'text.primary',
        }}
      >
        {lines.map((l, i) => {
          const tone = isDiff ? (l.startsWith('+') ? '#22c55e' : l.startsWith('-') ? '#ef4444' : null) : null;
          return (
            <Box key={i} component="span" sx={{ display: 'block', minHeight: '1.55em', whiteSpace: 'pre', ...(tone ? { color: tone, bgcolor: alpha(tone, 0.1) } : {}) }}>
              {l || ' '}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

/** Renders report markdown (paragraphs, lists, inline code, fenced code and diffs). */
export function Markdown({ children }: { children: string }) {
  return (
    <Box
      sx={{
        fontSize: 13, lineHeight: 1.7, color: 'text.primary',
        '& p': { m: 0, mb: 1.5 },
        '& ol, & ul': { m: 0, mb: 1.5, pl: 2.75 },
        '& li': { mb: 0.75, pl: 0.5 },
        '& strong': { fontWeight: 700 },
        '& > :last-child': { mb: 0 },
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          pre: ({ children: c }) => <>{c}</>,
          code: ({ node, className, children: c }: any) => {
            const text = String(c);
            if (!text.endsWith('\n') && !/language-/.test(className || '')) return <InlineCode>{c}</InlineCode>;
            return <CodeBlock code={text} lang={/language-(\w+)/.exec(className || '')?.[1]} title={parseFenceMeta(node?.data?.meta)} />;
          },
        }}
      >
        {children}
      </ReactMarkdown>
    </Box>
  );
}
